import { useCallback, useEffect, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { Welcome } from './components/Welcome';
import { Composer } from './components/Composer';
import { ReturnLink } from './components/ReturnLink';
import type { Draft } from './data/types';
import { EMPTY_SECTIONS } from './data/types';
import styles from './App.module.css';

type View = 'welcome' | 'composer';
const DRAFTS_KEY = 'negotiation-letter:drafts';
const LAST_OPENED_KEY = 'negotiation-letter:last-opened';

function newDraft(): Draft {
  const now = new Date().toISOString();
  return {
    id: `${Date.now()}-${Math.floor(Math.random() * 1e6).toString(36)}`,
    title: '',
    sections: { ...EMPTY_SECTIONS },
    createdAt: now,
    updatedAt: now,
  };
}

export function App() {
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    storage.get<Draft[]>(DRAFTS_KEY, [])
  );
  const [activeId, setActiveId] = useState<string | null>(() =>
    storage.get<string | null>(LAST_OPENED_KEY, null)
  );
  const [view, setView] = useState<View>(activeId ? 'composer' : 'welcome');

  // Persist drafts whenever they change
  useEffect(() => {
    storage.set(DRAFTS_KEY, drafts);
  }, [drafts]);

  // Persist last opened id
  useEffect(() => {
    storage.set(LAST_OPENED_KEY, activeId);
  }, [activeId]);

  const active = drafts.find((d) => d.id === activeId) ?? null;

  function begin() {
    const d = newDraft();
    setDrafts((arr) => [d, ...arr]);
    setActiveId(d.id);
    setView('composer');
  }

  function openDraft(id: string) {
    setActiveId(id);
    setView('composer');
  }

  function deleteDraft(id: string) {
    setDrafts((arr) => arr.filter((d) => d.id !== id));
    if (activeId === id) {
      setActiveId(null);
      setView('welcome');
    }
  }

  const updateActive = useCallback(
    (next: Draft) => {
      setDrafts((arr) => arr.map((d) => (d.id === next.id ? next : d)));
    },
    []
  );

  function goWelcome() {
    setView('welcome');
  }

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {view === 'composer' && (
          <button type="button" className={styles.backHome} onClick={goWelcome}>
            ← my drafts
          </button>
        )}
      </header>

      <main className={styles.stage}>
        {view === 'welcome' && (
          <Welcome
            drafts={drafts}
            onBegin={begin}
            onOpen={openDraft}
            onDelete={deleteDraft}
          />
        )}
        {view === 'composer' && active && (
          <Composer draft={active} onChange={updateActive} />
        )}
      </main>

      <footer className={styles.footer}>
        <p>drafts auto-save in your browser. nothing is uploaded anywhere.</p>
      </footer>
    </>
  );
}
